import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Trash2, Mail, Phone, Check, Clock, Archive } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts`, getAuthHeaders());
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}/status?status=${status}`, {}, getAuthHeaders());
      toast.success('Status updated');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, getAuthHeaders());
      toast.success('Enquiry deleted');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filteredContacts = contacts.filter(c => {
    if (filter === 'all') return true;
    return c.status === filter;
  });

  const statusConfig = {
    new: { label: 'New', color: 'bg-amber-100 text-amber-700', icon: Clock },
    contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-700', icon: Mail },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: Check },
    archived: { label: 'Archived', color: 'bg-gray-100 text-gray-600', icon: Archive },
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="admin-contacts">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">Contact Enquiries</h1>
          <p className="text-muted-foreground">View and manage form submissions</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {['all', 'new', 'contacted', 'completed', 'archived'].map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : statusConfig[f]?.label || f}
            {f !== 'all' && (
              <span className="ml-2 bg-white/20 px-1.5 rounded text-xs">
                {contacts.filter(c => c.status === f).length}
              </span>
            )}
          </Button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-border divide-y divide-border">
        {filteredContacts.length === 0 ? (
          <p className="p-6 text-center text-muted-foreground">No enquiries found</p>
        ) : (
          filteredContacts.map((contact) => {
            const StatusIcon = statusConfig[contact.status]?.icon || Clock;
            return (
              <div key={contact.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-semibold">{contact.name}</span>
                      {contact.student_age && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          {contact.student_age}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded inline-flex items-center gap-1 ${statusConfig[contact.status]?.color || 'bg-gray-100'}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig[contact.status]?.label || contact.status}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                      <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-primary">
                        <Mail className="w-4 h-4" /> {contact.email}
                      </a>
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="flex items-center gap-1 hover:text-primary">
                          <Phone className="w-4 h-4" /> {contact.phone}
                        </a>
                      )}
                    </div>
                    
                    <p className="text-sm text-muted-foreground bg-gray-50 p-3 rounded">{contact.message}</p>
                    
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(contact.created_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  
                  <div className="flex flex-col gap-1">
                    <select
                      value={contact.status}
                      onChange={(e) => updateStatus(contact.id, e.target.value)}
                      className="text-xs px-2 py-1 border border-border rounded focus:border-primary focus:outline-none"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="completed">Completed</option>
                      <option value="archived">Archived</option>
                    </select>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(contact.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminContacts;
